import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { tokenStore } from '@/lib/http-core';
import type { AdminMeResponse, AdminRole } from '@/lib/api-admin';
import { apiClient } from '@/lib/apiClient';

interface AuthContextValue {
  currentAdmin: AdminMeResponse | null;
  isHydrating: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [currentAdmin, setCurrentAdmin] = useState<AdminMeResponse | null>(null);
  const [isHydrating, setIsHydrating] = useState(true);

  const refresh = useCallback(async () => {
    if (!tokenStore.token) {
      setCurrentAdmin(null);
      setIsHydrating(false);
      return;
    }
    try {
      const [data] = await apiClient.request('get', '/admin/v1/me', {});
      setCurrentAdmin(data as AdminMeResponse);
    } catch {
      setCurrentAdmin(null);
    } finally {
      setIsHydrating(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.request('post', '/admin/v1/auth/logout', {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      });
    } catch {
      // ignore — clear local state regardless
    }
    tokenStore.clear();
    setCurrentAdmin(null);
    if (typeof window !== 'undefined') {
      window.location.assign('/login');
    }
  }, []);

  useEffect(() => {
    void refresh();
    const unsubscribe = tokenStore.subscribe((token) => {
      if (!token) {
        setCurrentAdmin(null);
        setIsHydrating(false);
      } else {
        void refresh();
      }
    });
    return unsubscribe;
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ currentAdmin, isHydrating, refresh, logout }),
    [currentAdmin, isHydrating, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within <AuthProvider>');
  return ctx;
};

export const useCurrentAdmin = (): AdminMeResponse | null => useAuthContext().currentAdmin;

export const useHasRole = (required: ReadonlyArray<AdminRole>): boolean => {
  const admin = useCurrentAdmin();
  if (!admin) return false;
  return required.some((r) => admin.roles.includes(r));
};
